// Personal Forecast Projection Engine
// Month-by-month balances for every account: recorded history first, then the projection.

import {
  ForecastAccount,
  CashFlowItem,
  FXRate,
  ForecastSettings,
  MonthlyProjection,
  MonthlyAccountProjection,
  Currency,
  CurrencySubtotal,
  FlowPeriod,
  CategoryFlowTable,
  CategoryFlowRow,
  YearSummary,
  IncomeStatement,
  IncomeStatementRow,
} from '../types/personalForecast';
import { calculateBiweeklySchedule } from './personalForecastCalculations';

const ALL_CURRENCIES: Currency[] = ['CAD', 'USD', 'GBP', 'EUR'];

// ---- Month helpers (local time; 'YYYY-MM') ----

export const monthKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export const currentMonth = (): string => monthKey(new Date());

export const addMonths = (month: string, count: number): string => {
  const [year, m] = month.split('-').map(Number);
  return monthKey(new Date(year, m - 1 + count, 1));
};

/** Inclusive count of months from `from` to `to`; 0 when `to` is earlier. */
export const monthsBetween = (from: string, to: string): number => {
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  return Math.max(0, (ty - fy) * 12 + (tm - fm) + 1);
};

/** Signed month count from `from` to `to`; negative when `to` is earlier, 0 for the same month. */
const monthIndexOf = (from: string, to: string): number => {
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  return (ty - fy) * 12 + (tm - fm);
};

const actualMonths = (acct: ForecastAccount): string[] =>
  Object.keys(acct.actuals || {}).filter(m => Number.isFinite(acct.actuals![m])).sort();

/** Latest month any account has a recorded balance, or null if none. */
export const latestActualMonth = (accounts: ForecastAccount[]): string | null => {
  const months = accounts.flatMap(actualMonths);
  return months.length ? months.sort()[months.length - 1] : null;
};

export const earliestActualMonth = (accounts: ForecastAccount[]): string | null => {
  const months = accounts.flatMap(actualMonths);
  return months.length ? months.sort()[0] : null;
};

/** The balance to show for an account today: its latest recorded one, else the opening balance. */
export const latestBalance = (acct: ForecastAccount): number => {
  const months = actualMonths(acct);
  return months.length ? acct.actuals![months[months.length - 1]] : acct.openingBalance;
};

/** Where the timeline begins: the history start if set, else the earliest recorded month, else now. */
export const timelineStart = (accounts: ForecastAccount[], settings: ForecastSettings): string => {
  const earliest = earliestActualMonth(accounts);
  const candidates = [settings.historyStartMonth, earliest].filter(Boolean) as string[];
  return candidates.length ? candidates.sort()[0] : currentMonth();
};

/** Months to project beyond the last actual (or beyond today when there is no history). */
function horizonMonths(settings: ForecastSettings, forecastStart: string): number {
  const naive = (() => {
    switch (settings.horizon) {
      case '1yr': return 12;
      case '5yr': return 60;
      case '10yr': return 120;
      case '20yr': return 240;
      case '30yr': return 360;
      case 'custom': return settings.customMonths || 60;
      case 'retirement':
        return settings.retirementDate ? Math.max(monthsBetween(forecastStart, settings.retirementDate), 12) : 60;
      default: return 60;
    }
  })();
  if (settings.horizon === 'retirement') return naive; // an exact life event, not a calendar year: never snapped
  // No stub years: the last month always lands on December, by whichever is fewer months to move.
  const naiveLast = addMonths(forecastStart, naive - 1);
  const lastMonthNum = Number(naiveLast.slice(5, 7));
  const forward = lastMonthNum === 12 ? 0 : 12 - lastMonthNum;
  const snappedLast = forward <= lastMonthNum ? addMonths(naiveLast, forward) : addMonths(naiveLast, -lastMonthNum);
  return monthsBetween(forecastStart, snappedLast);
}

/**
 * Cash flow amount for one month, handling every frequency including biweekly
 * three-paycheque months.
 */
/** How many times `growthMonth` has come round after `startDate`, up to and including `monthStr`. */
export const growthStepsBetween = (startDate: string, monthStr: string, growthMonth: number): number => {
  if (!startDate || monthStr <= startDate) return 0;
  let steps = 0;
  for (let m = addMonths(startDate, 1); m <= monthStr; m = addMonths(m, 1)) {
    if (Number(m.slice(5, 7)) === growthMonth) steps += 1;
  }
  return steps;
};

/** In season this month? Summer-only work is out of season in October whatever else is true. */
const isInSeason = (item: CashFlowItem, monthStr: string): boolean =>
  !item.activeMonths || item.activeMonths.length === 0
  || item.activeMonths.includes(Number(monthStr.slice(5, 7)));

const isActiveMonth = (item: CashFlowItem, monthStr: string): boolean => {
  if (item.startDate && item.startDate > monthStr) return false;
  if (item.endDate && item.endDate < monthStr) return false;
  return isInSeason(item, monthStr);
};

/**
 * Every item's amount for one month. Fixed items first, then items defined as a percentage of
 * others, resolved in passes so a share of a share still works.
 */
export function resolveMonthlyAmounts(
  cashFlows: CashFlowItem[], monthStr: string, monthIndex: number, balances?: Record<string, number>
): Record<string, number> {
  const amounts: Record<string, number> = {};
  const byId = new Map(cashFlows.map(cf => [cf.id, cf]));
  const forecastStart = addMonths(monthStr, -monthIndex);
  for (const cf of cashFlows) {
    if (cf.percentOf) continue;
    if (cf.percentOfBalance) {
      // A rate on what's actually owed this month. No balance left, no interest - which is why
      // this needs no end date, unlike a flat amount that would keep charging forever.
      const owed = Math.abs(balances?.[cf.percentOfBalance.accountId] ?? 0);
      amounts[cf.id] = isActiveMonth(cf, monthStr) ? owed * (cf.percentOfBalance.annualRate / 100) / 12 : 0;
    } else {
      amounts[cf.id] = getCashFlowAmountForMonth(cf, monthStr, monthIndex);
    }
  }
  const linked = cashFlows.filter(cf => cf.percentOf);
  for (let pass = 0; pass < 3; pass++) {
    for (const cf of linked) {
      if (!isActiveMonth(cf, monthStr)) { amounts[cf.id] = 0; continue; }
      const switchMonth = cf.growthPhaseYears !== undefined
        ? addMonths(cf.startDate || forecastStart, cf.growthPhaseYears * 12) : null;
      if (switchMonth !== null && monthStr >= switchMonth) {
        amounts[cf.id] = frozenGrowthAmount(cf, switchMonth, monthStr, byId, forecastStart);
      } else {
        const sources = cf.percentOf!.itemIds.flatMap(id => (byId.has(id) ? [{ item: byId.get(id)!, amount: amounts[id] ?? 0 }] : []));
        amounts[cf.id] = percentBase(sources) * (cf.percentOf!.percent / 100);
      }
    }
  }
  return amounts;
}

/**
 * A "% of" item that has passed its growth-phase switch stops tracking its sources: it freezes
 * at what they produced the month before the switch, then grows on its own from there at
 * longTermGrowthRate (lifestyle creep for 5 years, then held to inflation).
 */
function frozenGrowthAmount(
  cf: CashFlowItem, switchMonth: string, monthStr: string, byId: Map<string, CashFlowItem>,
  forecastStart: string, depth = 0
): number {
  const prevMonth = addMonths(switchMonth, -1);
  const prevIndex = monthIndexOf(forecastStart, prevMonth);
  // What the sources were worth on their own schedule, whether or not they were still running:
  // part-time pay as half your old salary has to survive that salary having stopped.
  const sources = cf.percentOf!.itemIds.flatMap(id => {
    const source = byId.get(id);
    return source ? [{ item: source, amount: sourceAmount(source, prevMonth, prevIndex, byId, forecastStart, depth) }] : [];
  });
  const frozenBase = percentBase(sources) * (cf.percentOf!.percent / 100);
  const rate = cf.longTermGrowthRate ?? cf.annualGrowthRate ?? 0;
  const monthsElapsed = monthIndexOf(switchMonth, monthStr);
  return frozenBase * Math.pow(1 + rate / 100, monthsElapsed / 12);
}

/**
 * What one source was worth in a given month, on its own schedule. A source can itself be a share
 * of other items - a career modelled as a run of phases, each picking up from the last - and
 * reading only its typed-in amount would return zero for every phase after the first. The depth cap
 * is a backstop: the editor already refuses to build a circular reference.
 */
function sourceAmount(
  cf: CashFlowItem, monthStr: string, monthIndex: number, byId: Map<string, CashFlowItem>,
  forecastStart: string, depth = 0
): number {
  if (!cf.percentOf || depth >= 8) return getCashFlowAmountForMonth(cf, monthStr, monthIndex, true);
  const switchMonth = cf.growthPhaseYears !== undefined
    ? addMonths(cf.startDate || forecastStart, cf.growthPhaseYears * 12) : null;
  if (switchMonth !== null && monthStr >= switchMonth) {
    return frozenGrowthAmount(cf, switchMonth, monthStr, byId, forecastStart, depth + 1);
  }
  const sources = cf.percentOf.itemIds.flatMap(id => {
    const source = byId.get(id);
    return source ? [{ item: source, amount: sourceAmount(source, monthStr, monthIndex, byId, forecastStart, depth + 1) }] : [];
  });
  return percentBase(sources) * (cf.percentOf.percent / 100);
}

/**
 * The base of a "% of" item. Sources that all flow one way simply add up (spending as 75% of
 * salary, HST on groceries). A mix of inflows and outflows is a net result, inflows less outflows,
 * so a partner's share of "rent minus costs" comes in when the property loses money and goes out
 * when it profits. A negative result flows the opposite way to the item that month.
 */
const percentBase = (sources: { item: CashFlowItem; amount: number }[]): number => {
  const mixed = sources.some(s => s.item.isInflow) && sources.some(s => !s.item.isInflow);
  return sources.reduce((sum, s) => sum + (mixed && !s.item.isInflow ? -s.amount : s.amount), 0);
};

/**
 * `ignoreDates` asks only "what would this have paid, on its own schedule?" - it looks past the
 * start and end dates so part-time pay can still be half a salary that has stopped. Seasonality is
 * NOT waived: summer-only income contributes nothing to a base taken in October either way.
 */
function getCashFlowAmountForMonth(
  item: CashFlowItem, monthStr: string, monthIndex: number, ignoreDates = false
): number {
  if (ignoreDates ? !isInSeason(item, monthStr) : !isActiveMonth(item, monthStr)) return 0;

  let baseAmount = item.amount;
  if (item.annualGrowthRate || item.longTermGrowthRate) {
    const rate1 = item.annualGrowthRate || 0;
    const rate2 = item.longTermGrowthRate ?? rate1;
    const phase = item.growthPhaseYears ?? Infinity;
    if (item.growthMonth) {
      // A raise effective every August: the amount steps up once per anniversary, not every month.
      // Past the growth phase, later steps compound at the long-term rate instead; nothing resets.
      const steps = growthStepsBetween(item.startDate, monthStr, item.growthMonth);
      const steps1 = Math.min(steps, phase);
      baseAmount *= Math.pow(1 + rate1 / 100, steps1) * Math.pow(1 + rate2 / 100, steps - steps1);
    } else if (monthIndex > 0) {
      const years1 = Math.min(monthIndex / 12, phase);
      baseAmount *= Math.pow(1 + rate1 / 100, years1) * Math.pow(1 + rate2 / 100, monthIndex / 12 - years1);
    }
  }

  switch (item.frequency) {
    case 'monthly':
      return baseAmount;
    case 'biweekly': {
      if (!item.firstPayDate) return (baseAmount * 26) / 12;
      const schedule = calculateBiweeklySchedule(item.firstPayDate, baseAmount, monthIndex + 2);
      return schedule.monthlyBreakdown.find(m => m.month === monthStr)?.totalAmount || 0;
    }
    case 'semi-monthly':
      return baseAmount * 2;
    case 'weekly':
      return (baseAmount * 52) / 12;
    case 'annual': {
      if (!item.startDate) return baseAmount / 12;
      return item.startDate.slice(5, 7) === monthStr.slice(5, 7) ? baseAmount : 0;
    }
    case 'quarterly': {
      if (!item.startDate) return monthIndex % 3 === 0 ? baseAmount : 0;
      const startM = new Date(item.startDate + '-01').getMonth();
      const curM = new Date(monthStr + '-01').getMonth();
      return (curM - startM + 12) % 3 === 0 ? baseAmount : 0;
    }
    case 'one-time':
      return item.startDate === monthStr ? baseAmount : 0;
    default:
      return baseAmount;
  }
}

/** FX rate between two currencies. 1 if the same or unknown. */
export function getFXRate(from: Currency, to: Currency, rates: FXRate[]): number {
  if (from === to) return 1;
  const direct = rates.find(r => r.fromCurrency === from && r.toCurrency === to);
  if (direct) return direct.rate;
  const inverse = rates.find(r => r.fromCurrency === to && r.toCurrency === from);
  if (inverse) return 1 / inverse.rate;
  return 1;
}

/** A linked account's balance: `percent` of its source accounts' balances this month, as a magnitude. */
export const linkedBalance = (acct: ForecastAccount, closingById: Record<string, number>): number => {
  const total = (acct.linkedTo?.accountIds || []).reduce((sum, id) => sum + (closingById[id] ?? 0), 0);
  return Math.abs(total * ((acct.linkedTo?.percent || 0) / 100));
};

/** Source accounts first, so a linked account always sees this month's balances. */
const inDependencyOrder = (accounts: ForecastAccount[]): ForecastAccount[] =>
  [...accounts.filter(a => !a.linkedTo), ...accounts.filter(a => a.linkedTo)];

/**
 * Main projection engine.
 *
 * Months up to the latest recorded balance are "actual": each account shows what the user
 * typed (carried forward across gaps), with no modelled flows. The forecast then runs from
 * that balance. Accounts whose history ends earlier are projected from their own last entry.
 * A linked account takes a percentage of other accounts' balances in the same month instead
 * of being projected, unless it has a recorded balance for that month.
 */
export function generateForecast(
  accounts: ForecastAccount[],
  cashFlows: CashFlowItem[],
  fxRates: FXRate[],
  settings: ForecastSettings
): MonthlyProjection[] {
  const start = timelineStart(accounts, settings);
  const lastActual = latestActualMonth(accounts);
  const forecastStart = lastActual ? addMonths(lastActual, 1) : start;
  const historyMonths = lastActual ? monthsBetween(start, lastActual) : 0;
  const monthCount = historyMonths + horizonMonths(settings, forecastStart);

  const balances: Record<string, number> = {};
  const firstActualByAccount: Record<string, string | null> = {};
  const lastActualByAccount: Record<string, string | null> = {};
  accounts.forEach(a => {
    balances[a.id] = a.openingBalance;
    const months = actualMonths(a);
    firstActualByAccount[a.id] = months.length ? months[0] : null;
    lastActualByAccount[a.id] = months.length ? months[months.length - 1] : null;
  });

  const projections: MonthlyProjection[] = [];
  /** The last twelve forecast months of real spending, for anything sized against what you spend. */
  const recentSpend: number[] = [];

  for (let mi = 0; mi < monthCount; mi++) {
    const monthStr = addMonths(start, mi);
    const forecastIndex = Math.max(0, mi - historyMonths);
    const acctProjections: Record<string, MonthlyAccountProjection> = {};
    let totalInflows = 0;
    let totalOutflows = 0;
    let totalAssets = 0;
    let afterTaxAssets = 0;
    let totalLiabilities = 0;
    let passiveIncome = 0;
    const warnings: string[] = [];

    const byCurrency = {} as Record<Currency, CurrencySubtotal>;
    for (const c of ALL_CURRENCIES) {
      byCurrency[c] = { totalAssets: 0, totalLiabilities: 0, netWorth: 0, totalInflows: 0, totalOutflows: 0 };
    }

    const closingThisMonth: Record<string, number> = {};
    const flows: Record<string, number> = {};
    // Several payments can target one debt in a month; each sees what the earlier ones left.
    const paidSoFar: Record<string, number> = {};
    // Inside recorded history the flows are whatever was recorded for the month (default nothing);
    // the forecast rules take over in the first month after the latest recorded balance.
    const inHistory = lastActual !== null && monthStr <= lastActual;
    const amounts: Record<string, number> = inHistory
      ? Object.fromEntries(cashFlows.map(cf => [cf.id, cf.actuals?.[monthStr] ?? 0]))
      : resolveMonthlyAmounts(cashFlows, monthStr, forecastIndex, balances);

    // You can't pay off more than you owe, and the bank wouldn't take it. Cap every transfer into
    // a debt at what's outstanding, so the cash leaving always matches the debt retired - without
    // this, a payment with no end date keeps draining cash into a mortgage that's already gone.
    for (const cf of cashFlows) {
      if (!cf.toAccountId) continue;
      const dest = accounts.find(a => a.id === cf.toAccountId);
      if (!dest || dest.side !== 'liability') continue;
      const outstanding = Math.max(0, Math.abs(balances[dest.id] ?? 0) - (paidSoFar[dest.id] || 0));
      const amt = amounts[cf.id] || 0;
      if (amt > outstanding) amounts[cf.id] = outstanding;
      paidSoFar[dest.id] = (paidSoFar[dest.id] || 0) + (amounts[cf.id] || 0);
    }

    const spendThisMonth = inHistory ? 0 : cashFlows.reduce((sum, cf) => {
      if (cf.isInflow || cf.toAccountId) return sum;
      const acct = accounts.find(a => a.id === cf.accountId);
      if (!acct || acct.bucket !== 'current') return sum;
      return sum + (amounts[cf.id] || 0) * getFXRate(acct.currency, settings.baseCurrency, fxRates);
    }, 0);
    if (!inHistory) {
      recentSpend.push(spendThisMonth);
      if (recentSpend.length > 12) recentSpend.shift();
    }
    // A year's average, so an annual bill doesn't make the target lurch in the month it lands.
    const averageMonthlySpend = recentSpend.length
      ? recentSpend.reduce((a, b) => a + b, 0) / recentSpend.length : 0;

    // Sweep rules: checked against last month's balance (this month's opening, still untouched
    // at this point), so a source and its destination never depend on each other within the same
    // month. In the forecast only; a source still inside its own recorded history never sweeps.
    const sweepOutflows: Record<string, number> = {};
    const sweepInflows: Record<string, number> = {};
    const sweepMoves: MonthlyProjection['sweepMoves'] = [];
    for (const acct of accounts) {
      const rule = acct.sweepRule;
      if (!rule) continue;
      const acctLastActual = lastActualByAccount[acct.id];
      if (acctLastActual !== null && monthStr <= acctLastActual) continue;
      // Two shapes of rule. "Keep N months of spending here" works out its own ceiling from what
      // you actually spend and moves the whole excess, so cash cannot pile up in a chequing
      // account for thirty years. The older fixed threshold and amount still work as before.
      const keepMonths = rule.keepMonthsOfSpending;
      const ceiling = keepMonths ? averageMonthlySpend * keepMonths : rule.aboveBalance;
      const excess = balances[acct.id] - ceiling;
      const amount = keepMonths ? excess : rule.transferAmount;
      if (excess > 0 && amount > 0) {
        sweepOutflows[acct.id] = (sweepOutflows[acct.id] || 0) + amount;
        sweepInflows[rule.toAccountId] = (sweepInflows[rule.toAccountId] || 0) + amount;
        sweepMoves.push({ fromAccountId: acct.id, toAccountId: rule.toAccountId, amount });
      }
    }

    for (const acct of inDependencyOrder(accounts)) {
      const opening = balances[acct.id];
      const acctLastActual = lastActualByAccount[acct.id];
      const isActual = acctLastActual !== null && monthStr <= acctLastActual;
      let inflows = sweepInflows[acct.id] || 0;
      let outflows = sweepOutflows[acct.id] || 0;
      let interest = 0;
      let growth = 0;
      let valuationChange = 0;
      let closing: number;

      // Flows through this account this month: recorded ones in history, modelled ones after.
      for (const cf of cashFlows) {
        const isSource = cf.accountId === acct.id;
        const isDestination = cf.toAccountId === acct.id;
        if (!isSource && !isDestination) continue;
        const amt = amounts[cf.id] || 0;
        if (amt) flows[cf.id] = amt;
        if (cf.toAccountId) {
          // A transfer: out of the source account, into the destination, same month. Landing in
          // a liability pays it down, so it counts as a reduction there too, not an increase.
          const destIsLiability = acct.side === 'liability';
          if (isSource) outflows += amt;
          if (isDestination) { if (destIsLiability) outflows += amt; else inflows += amt; }
        } else if (cf.isInflow) {
          inflows += amt;
        } else {
          outflows += amt;
        }
      }

      if (isActual && Number.isFinite(acct.actuals?.[monthStr])) {
        closing = acct.actuals![monthStr];
      } else if (isActual && monthStr < firstActualByAccount[acct.id]!) {
        // Before its first recorded balance an account has no history, so it shows nothing.
        closing = 0;
      } else if (acct.linkedTo) {
        closing = linkedBalance(acct, closingThisMonth);
        valuationChange = closing - opening - inflows + outflows;
      } else if (isActual) {
        // A blank month after the first record carries the previous recorded balance forward.
        closing = opening;
      } else {
        interest = opening * (acct.interestRate / 100) / 12;
        growth = opening * (acct.growthRate / 100) / 12;
        // Only what your assets earn is passive income; interest on a debt is a cost.
        if (acct.side === 'asset' && interest + growth > 0) {
          passiveIncome += interest + growth;
        }
        valuationChange = acct.monthlyChange || 0;
        closing = opening + inflows - outflows + interest + growth + valuationChange;
        // A debt paid off, or a depreciating asset run out, can't go past zero and come out the
        // other side negative - whether that came from a fixed monthly change or from flows
        // (a mortgage's last real payment). Current-bucket accounts are left alone so an
        // overdraft still shows.
        if (closing < 0 && (acct.side === 'liability' || acct.bucket !== 'current')) {
          // Only as much of the revaluation as the floor left room for actually happened: a car
          // worth 500 depreciating by 1000 lost 500, and once it reads zero it loses nothing at all.
          // Unwind the revaluation, never invent one - an overshooting payment is a real cash
          // movement and belongs in the reconciling line, not dressed up as a valuation loss.
          if (valuationChange < 0) valuationChange = Math.min(0, valuationChange - closing);
          closing = 0;
        }
      }

      balances[acct.id] = closing;
      closingThisMonth[acct.id] = acct.side === 'asset' ? closing : -Math.abs(closing);
      acctProjections[acct.id] = { openingBalance: opening, inflows, outflows, interest, growth, valuationChange, closingBalance: closing, isActual };

      const rate = getFXRate(acct.currency, settings.baseCurrency, fxRates);
      // Headline cash in/out is what moved through current accounts; a contribution into savings
      // or retirement (and now a debt paydown) is never cash, matching the Cash Flow view's own
      // "contributions land directly in savings or retirement and never touch cash" description.
      if (acct.bucket === 'current') {
        totalInflows += inflows * rate;
        totalOutflows += outflows * rate;
      }
      if (acct.side === 'asset') {
        totalAssets += closing * rate;
        // An RRSP, a pension or CPP is taxed on the way out, so only part of the balance is really
        // yours. Anything not marked is taken at face value rather than quietly discounted.
        afterTaxAssets += closing * rate * ((acct.afterTaxPercent ?? 100) / 100);
        byCurrency[acct.currency].totalAssets += closing;
      } else {
        totalLiabilities += Math.abs(closing) * rate;
        byCurrency[acct.currency].totalLiabilities += Math.abs(closing);
      }
      byCurrency[acct.currency].totalInflows += inflows;
      byCurrency[acct.currency].totalOutflows += outflows;

      if (closing < 0 && acct.side === 'asset' && acct.bucket === 'current') {
        warnings.push(`${acct.name} overdraft: ${closing.toFixed(0)}`);
      }
    }

    for (const c of ALL_CURRENCIES) {
      byCurrency[c].netWorth = byCurrency[c].totalAssets - byCurrency[c].totalLiabilities;
    }

    const netWorth = totalAssets - totalLiabilities;
    projections.push({
      month: monthStr,
      source: lastActual && monthStr <= lastActual ? 'actual' : 'forecast',
      accounts: acctProjections,
      flows,
      sweepMoves,
      totalInflows,
      totalOutflows,
      netCashFlow: totalInflows - totalOutflows,
      totalAssets,
      totalLiabilities,
      netWorth,
      netWorthAfterTax: afterTaxAssets - totalLiabilities,
      passiveIncome,
      passiveIncomeRatio: spendThisMonth > 0 ? passiveIncome / spendThisMonth : 0,
      byCurrency,
      combinedAssets: totalAssets,
      combinedLiabilities: totalLiabilities,
      combinedNetWorth: netWorth,
      warnings,
    });
  }

  return projections;
}

/** Average monthly amount of a cash-flow item, allowing for frequency, seasonal months and shares of other items. */
export const monthlyEquivalent = (item: CashFlowItem, allItems: CashFlowItem[] = []): number => {
  if (item.percentOf) {
    const sources = item.percentOf.itemIds.flatMap(id => {
      const source = allItems.find(cf => cf.id === id);
      return source && !source.percentOf ? [{ item: source, amount: monthlyEquivalent(source) }] : [];
    });
    return percentBase(sources) * (item.percentOf.percent / 100);
  }
  const perYear: Record<CashFlowItem['frequency'], number> = {
    monthly: 12, biweekly: 26, 'semi-monthly': 24, weekly: 52, quarterly: 4, annual: 1, 'one-time': 0
  };
  let months = 12;
  if (item.activeMonths && item.activeMonths.length > 0 && item.frequency === 'monthly') months = item.activeMonths.length;
  const yearly = item.frequency === 'monthly' ? item.amount * months : item.amount * perYear[item.frequency];
  return yearly / 12;
};

/**
 * Cash flow by category across months or calendar years, in the base currency.
 * Items on current-bucket accounts are cash; the rest are contributions into savings or retirement.
 */
export const aggregateFlowsByCategory = (
  projections: MonthlyProjection[],
  cashFlows: CashFlowItem[],
  accounts: ForecastAccount[],
  fxRates: FXRate[],
  settings: ForecastSettings,
  period: FlowPeriod
): CategoryFlowTable => {
  const accountById = new Map(accounts.map(a => [a.id, a]));
  const itemById = new Map(cashFlows.map(cf => [cf.id, cf]));
  const periodOf = (month: string) => (period === 'year' ? month.slice(0, 4) : month);
  const periods = Array.from(new Set(projections.map(p => periodOf(p.month))));
  const zeroes = () => Object.fromEntries(periods.map(p => [p, 0])) as Record<string, number>;
  const byCategory = new Map<string, CategoryFlowRow>();
  const cashNet = zeroes();   // what reached a current account
  const netTotal = zeroes();  // every account, transfers cancelling: what you earned and kept

  /** A transfer always leaves, whatever isInflow says - that is what toAccountId means. */
  const isMoneyIn = (cf: CashFlowItem) => !cf.toAccountId && cf.isInflow;

  const label = (cf: CashFlowItem) => {
    if (!cf.toAccountId) return cf.name || cf.category;
    return `${cf.name || cf.category} → ${accountById.get(cf.toAccountId)?.name || '?'}`;
  };

  /** Find or start the row a category reports on. */
  const rowFor = (category: string): CategoryFlowRow => {
    let row = byCategory.get(category);
    if (!row) {
      row = { category, items: [], netCash: zeroes(), netNonCash: zeroes(),
        netWorth: zeroes(), transfersOut: zeroes(), total: 0 };
      byCategory.set(category, row);
    }
    return row;
  };

  for (const p of projections) {
    const key = periodOf(p.month);

    for (const move of p.sweepMoves) {
      const from = accountById.get(move.fromAccountId);
      const to = accountById.get(move.toAccountId);
      if (!from || !to) continue;
      const amount = move.amount * getFXRate(from.currency, settings.baseCurrency, fxRates);
      const label = `Sweep: ${from.name || '?'} → ${to.name || '?'}`;
      const row = rowFor(label);
      row.transfersOut[key] += amount;
      if (from.bucket === 'current') {
        row.netCash[key] -= amount;
        cashNet[key] -= amount;
      } else {
        row.netNonCash[key] -= amount;
      }
      let item = row.items.find(i => i.label === label);
      if (!item) {
        item = { label, isInflow: false, isTransfer: true, byPeriod: zeroes(), total: 0 };
        row.items.push(item);
      }
      item.byPeriod[key] -= amount;
      item.total -= amount;
    }

    for (const [itemId, rawAmount] of Object.entries(p.flows)) {
      const cf = itemById.get(itemId);
      const acct = cf ? accountById.get(cf.accountId) : undefined;
      if (!cf || !acct) continue;

      const row = rowFor(cf.category);

      const amount = rawAmount * getFXRate(acct.currency, settings.baseCurrency, fxRates);
      const movedIn = isMoneyIn(cf);
      const signed = movedIn ? amount : -amount;

      // Only current accounts are cash. A contribution arriving straight in an RRSP never hits the
      // bank, so counting it as cash would claim money that was never there.
      if (acct.bucket === 'current') {
        row.netCash[key] += signed;
        cashNet[key] += signed;
      } else {
        row.netNonCash[key] += signed;
      }
      // Net worth ignores transfers: the money moved between accounts you own either way.
      if (movedIn) {
        row.netWorth[key] += amount;
        netTotal[key] += amount;
      } else if (cf.toAccountId) {
        row.transfersOut[key] += amount;
      } else {
        row.netWorth[key] -= amount;
        netTotal[key] -= amount;
      }

      let item = row.items.find(i => i.label === label(cf));
      if (!item) {
        item = { label: label(cf), isInflow: movedIn, isTransfer: !!cf.toAccountId,
          byPeriod: zeroes(), total: 0 };
        row.items.push(item);
      }
      item.byPeriod[key] += signed;
      item.total += signed;
    }
  }

  const rows = Array.from(byCategory.values())
    .filter(row => periods.some(p =>
      Math.abs(row.netCash[p]) > 0.005 || Math.abs(row.netNonCash[p]) > 0.005))
    .map(row => {
      row.total = periods.reduce((sum, p) => sum + row.netCash[p] + row.netNonCash[p], 0);
      row.items.sort((a, b) => b.total - a.total); // money in first, biggest cost last
      return row;
    })
    .sort((a, b) => b.total - a.total);

  return { periods, rows, cashNet, netTotal };
};

/** Roll monthly projections up into calendar years: year-end balances, flows summed across the year. */
export const summarizeByYear = (projections: MonthlyProjection[]): YearSummary[] => {
  const years: YearSummary[] = [];
  projections.forEach((p, index) => {
    const year = p.month.slice(0, 4);
    let current = years[years.length - 1];
    if (!current || current.year !== year) {
      current = {
        year, months: 0, firstMonth: p.month, lastMonth: p.month, lastIndex: index, source: p.source,
        accounts: Object.fromEntries(Object.entries(p.accounts).map(([id, a]) => [id, { ...a }])),
        totalInflows: 0, totalOutflows: 0, netCashFlow: 0,
        totalAssets: 0, totalLiabilities: 0, netWorth: 0, netWorthAfterTax: 0, passiveIncomeRatio: 0, warnings: [],
      };
      years.push(current);
    }
    current.months += 1;
    current.lastMonth = p.month;
    current.lastIndex = index;
    if (current.source !== p.source) current.source = 'mixed';
    for (const [id, a] of Object.entries(p.accounts)) {
      const y = current.accounts[id];
      if (current.months === 1) continue; // first month already copied whole
      y.inflows += a.inflows; y.outflows += a.outflows; y.interest += a.interest; y.growth += a.growth;
      y.valuationChange += a.valuationChange;
      y.closingBalance = a.closingBalance;
      y.isActual = y.isActual && a.isActual;
    }
    current.totalInflows += p.totalInflows;
    current.totalOutflows += p.totalOutflows;
    current.netCashFlow += p.netCashFlow;
    current.totalAssets = p.totalAssets;
    current.totalLiabilities = p.totalLiabilities;
    current.netWorth = p.netWorth;
    current.netWorthAfterTax = p.netWorthAfterTax;
    current.passiveIncomeRatio = p.passiveIncomeRatio;
    current.warnings = p.warnings;
  });
  for (let i = 1; i < years.length; i++) years[i].netWorthChange = years[i].netWorth - years[i - 1].netWorth;
  return years;
};

/**
 * Everything closingBalance moved by that nothing on the page explains: a recorded balance that
 * stepped with no flow behind it (a restatement, or an entry never captured) and any debt-floor
 * clamp. Flows, interest, growth and the fixed revaluation are all accounted for elsewhere, so a
 * healthy forecast leaves this at zero. Works on a month's or a year's projection, same shape either way.
 */
export const otherBalanceChange = (ap: MonthlyAccountProjection): number =>
  ap.closingBalance - ap.openingBalance - ap.inflows + ap.outflows - ap.interest - ap.growth - ap.valuationChange;

/**
 * Net cash flow, net investing, and growth split by account - built to reconcile exactly to the
 * change in net worth each period. "Net investing" is everything moving into or out of an account
 * that isn't current-bucket cash (contributions, and now debt paydown) - a pure transfer cancels
 * across the two, so only real income survives. Each account's passive row combines its interest,
 * growth rate and revaluation, and anything left unexplained lands in otherReconciling. All of it is
 * signed so a gain - an asset growing or a liability shrinking - is positive, as net worth reads it.
 */
export const buildIncomeStatement = (
  projections: MonthlyProjection[],
  years: YearSummary[],
  accounts: ForecastAccount[],
  fxRates: FXRate[],
  settings: ForecastSettings,
  period: FlowPeriod
): IncomeStatement => {
  const periodOf = (month: string) => (period === 'year' ? month.slice(0, 4) : month);
  const periods = Array.from(new Set(projections.map(p => periodOf(p.month))));
  const zero = () => Object.fromEntries(periods.map(p => [p, 0])) as Record<string, number>;

  const netCashFlow = zero();
  const netInvesting = zero();
  const otherReconciling = zero();
  const passiveByAccount = new Map(accounts.map(a => [a.id, zero()]));

  for (const p of projections) {
    const label = periodOf(p.month);
    netCashFlow[label] += p.netCashFlow; // already current-bucket only, already base currency
    for (const acct of accounts) {
      const ap = p.accounts[acct.id];
      if (!ap) continue;
      const sign = acct.side === 'asset' ? 1 : -1;
      const rate = getFXRate(acct.currency, settings.baseCurrency, fxRates);
      if (acct.bucket !== 'current') {
        netInvesting[label] += sign * (ap.inflows - ap.outflows) * rate;
      }
      // Interest, the growth rate and a dollar revaluation are the same thing earned three ways.
      passiveByAccount.get(acct.id)![label] += sign * (ap.interest + ap.growth + ap.valuationChange) * rate;
      otherReconciling[label] += sign * otherBalanceChange(ap) * rate;
    }
  }

  const passiveRows: IncomeStatementRow[] = accounts
    .map(a => {
      const byPeriod = passiveByAccount.get(a.id)!;
      const total = Object.values(byPeriod).reduce((sum, v) => sum + v, 0);
      return { label: a.name || '(unnamed)', byPeriod, total };
    })
    .filter(row => Object.values(row.byPeriod).some(v => Math.abs(v) > 0.005))
    .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));

  // The change in net worth each period, straight from the engine's own totals - the number every
  // other row here should sum to. The very first period has nothing before it to compare against,
  // so it's left out entirely rather than defaulting to a misleading zero.
  const changeInNetWorth: Record<string, number | undefined> = {};
  if (period === 'year') {
    for (const y of years) changeInNetWorth[y.year] = y.netWorthChange;
  } else {
    projections.forEach((p, i) => { changeInNetWorth[p.month] = i > 0 ? p.netWorth - projections[i - 1].netWorth : undefined; });
  }

  return { periods, netCashFlow, netInvesting, passiveRows, otherReconciling, changeInNetWorth };
};
